import { useMemo } from "react"

import { Button } from "@aix/ui"

import { trpc } from "../../lib/trpc"

interface Run {
  id: number
  agent_id: number
  status: "queued" | "running" | "completed" | "failed"
  prompt?: string | null
  branch?: string | null
  pr_url?: string | null
  error?: string | null
  output?: string | null
  triggered_by?: string | null
  started_at: string
  ended_at?: string | null
  executor_job_id?: string | null
}

interface ParsedJobEvent {
  id: string
  kind: "assistant" | "error" | "hook" | "metric" | "system" | "tool"
  label: string
  detail: string
  timestamp: string | null
}

function fmtDuration(start?: string | null, end?: string | null): string {
  if (!start) return ""
  const s = new Date(start).getTime()
  const e = end ? new Date(end).getTime() : Date.now()
  const sec = Math.max(0, Math.round((e - s) / 1000))
  if (sec < 60) return `${sec}s`
  const m = Math.floor(sec / 60)
  if (m < 60) return `${m}m ${sec % 60}s`
  return `${Math.floor(m / 60)}h ${m % 60}m`
}

function readRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : null
}

function readText(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null
}

function formatCost(value: unknown): string | null {
  return typeof value === "number" ? `$${value.toFixed(4)}` : null
}

function stringifyDetail(value: unknown): string {
  if (typeof value === "string") return value
  if (value == null) return ""
  return JSON.stringify(value)
}

function parseJobEvent(line: string, index: number): ParsedJobEvent | null {
  const parsed = readRecord(JSON.parse(line) as unknown)
  if (!parsed) return null
  const type = readText(parsed.type) ?? "event"
  const timestamp = readText(parsed.timestamp) ?? readText(parsed.created_at)

  if (type === "assistant") {
    const message = readRecord(parsed.message)
    const content = Array.isArray(message?.content) ? message.content : []
    const text = content
      .map((item) => readRecord(item))
      .map((item) => readText(item?.text))
      .filter((item): item is string => Boolean(item))
      .join("\n")
    return { id: `${index}-${type}`, kind: "assistant", label: "Assistant", detail: text || "Assistant message", timestamp }
  }

  if (type === "hook_started") {
    const hookName = readText(parsed.hook_name) ?? "Hook"
    return { id: `${index}-${type}`, kind: "hook", label: hookName, detail: "Started", timestamp }
  }

  if (type === "hook_response") {
    const hookName = readText(parsed.hook_name) ?? "Hook"
    const stdout = readText(parsed.stdout)
    const stderr = readText(parsed.stderr)
    return { id: `${index}-${type}`, kind: stderr ? "error" : "hook", label: hookName, detail: stderr ?? stdout ?? "Completed", timestamp }
  }

  if (type === "result") {
    const durationMs = typeof parsed.duration_ms === "number" ? `${Math.round(parsed.duration_ms / 1000)}s` : null
    const cost = formatCost(parsed.total_cost_usd)
    const reason = readText(parsed.terminal_reason) ?? readText(parsed.subtype) ?? "completed"
    const details = [reason, durationMs, cost].filter(Boolean).join(" · ")
    return { id: `${index}-${type}`, kind: "metric", label: "Result", detail: details || "Run finished", timestamp }
  }

  if (type === "notification" || type === "system" || type === "init") {
    const message = readText(parsed.message) ?? readText(parsed.subtype) ?? stringifyDetail(parsed)
    return { id: `${index}-${type}`, kind: "system", label: type, detail: message, timestamp }
  }

  if (type === "rate_limit_event") {
    return { id: `${index}-${type}`, kind: "metric", label: "Rate limit", detail: stringifyDetail(parsed), timestamp }
  }

  return { id: `${index}-${type}`, kind: "tool", label: type, detail: stringifyDetail(parsed), timestamp }
}

function parseJobEvents(text?: string): ParsedJobEvent[] {
  if (!text) return []
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .flatMap((line, index) => {
      try {
        const event = parseJobEvent(line, index)
        return event ? [event] : []
      } catch {
        return [{ id: `${index}-raw`, kind: "system" as const, label: "Raw", detail: line, timestamp: null }]
      }
    })
}

function getEventClass(kind: ParsedJobEvent["kind"]): string {
  if (kind === "error") return "border-destructive bg-destructive/10 text-destructive"
  if (kind === "assistant") return "border-blue-500/40 bg-blue-500/10 text-foreground"
  if (kind === "metric") return "border-emerald-500/40 bg-emerald-500/10 text-foreground"
  if (kind === "hook") return "border-amber-500/40 bg-amber-500/10 text-foreground"
  return "border-border bg-secondary text-foreground"
}

/**
 * Side drawer with the full output / error / branch / PR for one run.
 *
 * Live-refetches every 2s while the run is still in flight so the output streams in;
 * stops polling once `completed` or `failed`. Falls back to the per-agent runs list
 * so we can render even for runs older than the /api/jobs window.
 */
export function RunLogDrawer({ runId, onClose }: { runId: number; onClose: () => void }) {
  const query = trpc.brainAgents.runById.useQuery(
    { runId },
    {
      refetchInterval: (data) => {
        const status = (data as { status?: string } | undefined)?.status
        return status === "queued" || status === "running" ? 2000 : false
      },
    },
  )

  const run = query.data as unknown as Run | undefined
  const logQuery = trpc.brainAgents.runLog.useQuery(
    { runId, tail: 120 },
    {
      enabled: Boolean(run),
      refetchInterval: run?.status === "queued" || run?.status === "running" ? 2000 : false,
    },
  )
  const jobEvents = useMemo(() => parseJobEvents(logQuery.data?.text), [logQuery.data?.text])

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose} role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-background/40" />
      <div
        className="relative w-full max-w-2xl bg-background border-l-2 border-border h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-background border-b-2 border-border p-4 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h2 className="font-bold text-foreground">
              Run #{runId}
              {run && (
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  agent {run.agent_id} · {run.status} · {fmtDuration(run.started_at, run.ended_at)}
                </span>
              )}
            </h2>
          </div>
          <Button size="sm" variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>

        <div className="p-4 space-y-4">
          {query.isLoading && <div className="text-sm text-muted-foreground">Loading run…</div>}
          {query.error && (
            <div className="border-2 border-destructive bg-destructive/10 text-destructive p-2 text-sm">
              {query.error.message}
            </div>
          )}

          {run && (
            <>
              <DetailRow label="Status" value={run.status} />
              <DetailRow label="Triggered by" value={run.triggered_by ?? "—"} />
              <DetailRow label="Started" value={new Date(run.started_at).toLocaleString()} />
              <DetailRow label="Ended" value={run.ended_at ? new Date(run.ended_at).toLocaleString() : "—"} />
              <DetailRow label="Branch" value={run.branch ?? "—"} mono />
              {run.executor_job_id && <DetailRow label="Job id" value={run.executor_job_id} mono />}
              {run.pr_url && (
                <DetailRow
                  label="PR"
                  value={
                    <a
                      href={run.pr_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-foreground underline break-all"
                    >
                      {run.pr_url}
                    </a>
                  }
                />
              )}

              {run.prompt && (
                <div>
                  <div className="text-xs font-bold uppercase text-muted-foreground mb-1">Prompt</div>
                  <pre className="border-2 border-border bg-secondary p-2 text-xs font-mono whitespace-pre-wrap break-words text-foreground">
                    {run.prompt}
                  </pre>
                </div>
              )}

              {run.error && (
                <div>
                  <div className="text-xs font-bold uppercase text-destructive mb-1">Error</div>
                  <pre className="border-2 border-destructive bg-destructive/10 p-2 text-xs font-mono whitespace-pre-wrap break-words text-destructive">
                    {run.error}
                  </pre>
                </div>
              )}

              <div>
                <div className="text-xs font-bold uppercase text-muted-foreground mb-1 flex items-center justify-between">
                  <span>Output</span>
                  {(run.status === "running" || run.status === "queued") && (
                    <span className="flex items-center gap-1 text-[10px] normal-case font-normal">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" /> streaming
                    </span>
                  )}
                </div>
                <pre className="border-2 border-border bg-secondary p-2 text-xs font-mono whitespace-pre-wrap break-words text-foreground min-h-[120px]">
                  {run.output || (run.status === "running" ? "…" : "(no output)")}
                </pre>
              </div>

              <div>
                <div className="text-xs font-bold uppercase text-muted-foreground mb-1 flex items-center justify-between">
                  <span>Job messages</span>
                  <span className="normal-case font-normal">{jobEvents.length} parsed</span>
                </div>
                {logQuery.isLoading && <div className="text-sm text-muted-foreground">Loading job messages…</div>}
                {logQuery.error && (
                  <div className="border-2 border-destructive bg-destructive/10 text-destructive p-2 text-sm">
                    {logQuery.error.message}
                  </div>
                )}
                {!logQuery.isLoading && !logQuery.error && jobEvents.length === 0 && (
                  <div className="border-2 border-border bg-secondary p-2 text-sm text-muted-foreground">
                    No job messages found.
                  </div>
                )}
                <div className="space-y-2">
                  {jobEvents.map((event) => (
                    <div key={event.id} className={`border-2 p-2 text-xs ${getEventClass(event.kind)}`}>
                      <div className="flex items-center justify-between gap-3 font-bold">
                        <span>{event.label}</span>
                        {event.timestamp && <span className="font-normal text-muted-foreground">{event.timestamp}</span>}
                      </div>
                      <pre className="mt-1 whitespace-pre-wrap break-words font-mono text-[11px] leading-5">
                        {event.detail}
                      </pre>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function DetailRow({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex gap-3 text-sm">
      <span className="text-xs font-bold uppercase text-muted-foreground w-28 shrink-0 pt-0.5">{label}</span>
      <span className={mono ? "font-mono text-foreground break-all" : "text-foreground"}>{value}</span>
    </div>
  )
}
